const DEFAULT_COUNTER_ID = 104741583
const YANDEX_CLICK_ID_STORAGE_KEY = 'analytics.yandex.yclid'
const YANDEX_CLICK_ID_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000

type StoredYandexClickId = {
  value: string
  capturedAtMs: number
}

declare global {
  interface Window {
    ym?: (...args: any[]) => void
    dataLayer?: Array<Record<string, unknown>>
  }
}

export function resolveCounterId(): number {
  const raw = (import.meta as any).env?.VITE_YM_COUNTER_ID
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_COUNTER_ID
}

export function ensureYandexDataLayer(): Array<Record<string, unknown>> | null {
  if (typeof window === 'undefined') return null
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = []
  }
  return window.dataLayer
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  const prefix = `${encodeURIComponent(name)}=`
  const part = document.cookie.split(';').map(value => value.trim()).find(value => value.startsWith(prefix))
  if (!part) return undefined
  try {
    return decodeURIComponent(part.slice(prefix.length))
  } catch {
    return part.slice(prefix.length)
  }
}

function normalizeClientId(value?: string | null): string | undefined {
  const normalized = String(value || '').trim()
  return /^\d{1,32}$/.test(normalized) ? normalized : undefined
}

function normalizeClickId(value?: string | null): string | undefined {
  const normalized = String(value || '').trim()
  return /^[A-Za-z0-9._~-]{1,255}$/.test(normalized) ? normalized : undefined
}

export function captureYandexClickId(): string | undefined {
  if (typeof window === 'undefined') return undefined
  const fromUrl = normalizeClickId(new URLSearchParams(window.location.search).get('yclid'))
  if (fromUrl) {
    try {
      const stored: StoredYandexClickId = { value: fromUrl, capturedAtMs: Date.now() }
      window.localStorage.setItem(YANDEX_CLICK_ID_STORAGE_KEY, JSON.stringify(stored))
    } catch {
      // Attribution must never interrupt navigation.
    }
    return fromUrl
  }
  return readYandexClickId()
}

export function readYandexClickId(): string | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    const raw = window.localStorage.getItem(YANDEX_CLICK_ID_STORAGE_KEY)
    if (raw) {
      const stored = JSON.parse(raw) as StoredYandexClickId
      const value = normalizeClickId(stored?.value)
      if (value && Number.isFinite(stored?.capturedAtMs) && Date.now() - stored.capturedAtMs <= YANDEX_CLICK_ID_MAX_AGE_MS) {
        return value
      }
      window.localStorage.removeItem(YANDEX_CLICK_ID_STORAGE_KEY)
    }
  } catch {
    // Fall back to the cookie below.
  }
  return normalizeClickId(readCookie('yclid'))
}

export function identifyYandexUser(userId: number | string): boolean {
  if (typeof window === 'undefined' || typeof window.ym !== 'function') return false
  const normalized = String(userId).trim()
  if (!/^\d+$/.test(normalized)) return false
  try {
    window.ym(resolveCounterId(), 'setUserID', normalized)
    return true
  } catch {
    return false
  }
}

export async function getYandexCheckoutAttribution(timeoutMs = 700): Promise<{
  yandexClientId?: string
  yandexClickId?: string
}> {
  const yandexClickId = captureYandexClickId()
  if (typeof window === 'undefined') return { yandexClickId }

  const cookieClientId = normalizeClientId(readCookie('_ym_uid'))
  if (typeof window.ym !== 'function') {
    return { yandexClientId: cookieClientId, yandexClickId }
  }

  const yandexClientId = await new Promise<string | undefined>((resolve) => {
    let settled = false
    const finish = (value?: string | null) => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      resolve(normalizeClientId(value) || cookieClientId)
    }
    const timer = window.setTimeout(() => finish(), timeoutMs)
    try {
      window.ym?.(resolveCounterId(), 'getClientID', (clientId: string) => finish(clientId))
    } catch {
      finish()
    }
  })

  return { yandexClientId, yandexClickId }
}

export function reachYandexGoal(goal: string, params?: Record<string, unknown>): boolean {
  if (typeof window === 'undefined' || typeof window.ym !== 'function') return false
  const counterId = resolveCounterId()
  try {
    window.ym(counterId, 'reachGoal', goal, params || {})
    return true
  } catch {
    return false
  }
}

export function pushYandexEcommercePurchase(payload: {
  orderId: string
  revenue: number
  currency: string
  productId: string
  productName: string
  quantity?: number
}): boolean {
  if (typeof window === 'undefined') return false
  try {
    const dataLayer = ensureYandexDataLayer()
    if (!dataLayer) return false

    const quantity = typeof payload.quantity === 'number' && payload.quantity > 0 ? payload.quantity : 1
    dataLayer.push({
      ecommerce: {
        currencyCode: payload.currency,
        purchase: {
          actionField: {
            id: payload.orderId,
            revenue: payload.revenue
          },
          products: [
            {
              id: payload.productId,
              name: payload.productName,
              price: payload.revenue,
              quantity
            }
          ]
        }
      }
    })
    return true
  } catch {
    return false
  }
}
